<template>
  <div>
    <div
      v-if="address"
      :id="formName + '-form'"
      :class="'woocommerce-' + formName + '-fields'"
    >
      <h2
        v-if="formName === 'billing' && !hideTitle"
        class="checkout__subsection__heading"
      >
        {{ $modeoL10n.billing_information }}
      </h2>

      <div
        v-if="formName === 'shipping'"
        class="checkout-form-additional checkout-form-additional--shipping-address"
      >
        <label class="checkbox">
          <input
            id="ship-to-different-address-checkbox"
            v-model="useShippingAddress"
            type="checkbox"
            name="ship_to_different_address"
            class="checkbox-input"
            @change="addressChanged"
            @keydown.enter.prevent
          >
          <span class="checkbox-label">{{ $modeoL10n.separate_shipping_address }}</span>
        </label>
      </div>

      <div v-show="formName === 'billing' || useShippingAddress">
        <h2
          v-if="formName === 'shipping'"
          class="checkout__subsection__heading"
        >
          {{ $modeoL10n.shipping_address }}
        </h2>
        <div
          v-show="hasErrors"
          class="address-form__errors error-notification"
        >
          {{ $modeoL10n.errors_in_form }} {{ $modeoL10n.errors_in_address_form }}
        </div>
        <div
          :key="refresh"
          :class="'woocommerce-' + formName +'-fields__field-wrapper'"
        >
          <div
            v-for="(field,key) in formFields"
            :id="key + '_field'"
            :key="key"
            class="form-row"
            :class="field.class.join(' ')"
          >
            <label
              :for="key"
              :class="{'form-label-has-error': errors.has(key) }"
            >{{ field.label }}
              <abbr
                v-if="field.required"
                title="vaaditaan"
                class="required"
              >*</abbr>
            </label>

            <template v-if="field.type === 'country'">
              <strong>{{ $modeoL10n.finland }}</strong>
              <input
                :id="key"
                type="hidden"
                :name="key"
                value="FI"
              >
            </template>

            <input
              v-if="field.type != 'country'"
              :id="key"
              v-model="address[getIndex(key)]"
              v-validate="getValidations(field, key)"
              type="text"
              :name="key"
              placeholder=""
              :autocomplete="field.autocomplete"
              class="input-text"
              :disabled="key === formName + '_country'"
              :class="{'form-field-has-error': errors.has(key) }"
              @change="fieldChanged(field)"
              @blur="fieldFocusOut(key)"
              @keydown.enter.prevent
            >

            <span
              v-if="errors.has(key)"
              class="form-field-error-message"
            >{{ errors.first(key) }}</span>
          </div>
        </div>
      </div>

      <div
        v-if="formName === 'billing' && !userLoggedIn && !hideRegister"
        class="checkout-form-additional checkout-form-additional--register"
      >
        <label class="checkbox">
          <input
            v-model="register"
            type="checkbox"
            name="createaccount"
            value="1"
            class="checkbox-input"
            @keydown.enter.prevent
          >
          <span class="checkbox-label">{{ $modeoL10n.create_account }}</span>
        </label>

        <div v-if="register">
          <p
            id="account_password_field"
            data-priority=""
            class="form-row validate-required"
          >
            <label
              for="account_password"
              :class="{'form-label-has-error': errors.has('password') }"
            >{{ $modeoL10n.select_password }} <abbr
              title="vaaditaan"
              class="required"
            >*</abbr></label>
            <input
              id="account_password"
              v-model="password"
              v-validate="'required'"
              type="password"
              name="account_password"
              :placeholder="$modeoL10n.password"
              value=""
              class="input-text"
              :class="{'form-field-has-error': errors.has('password') }"
              @keydown.enter.prevent
            >
            <span
              v-if="errors.has('password')"
              class="form-field-error-message"
            >{{ errors.first('password') }}</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    import { EventBus } from '../../event-bus.js';
    import _ from 'lodash';

    /* TODO: Country hardcoded to FI */

    export default {
        props: {
            defaultValues: {
                type: Object,
                default: function(){
                    return {};
                },
            },
            formFields: {
                type: Object,
                default: function () {
                    return {};
                },
            },
            formName: {
                type: String,
                default: '',
            },
            userLoggedIn: Boolean,
            hideTitle: Boolean,
            hideRegister: Boolean,
            skipInitialUpdate: {
                type: Boolean,
                default: true,
            },

        },
        data : function () {
            return {
                address: {},
                useShippingAddress: false,
                formValidated: false,
                register: false,
                password: "",
                refresh: 0,
            };
        },
        computed: {
            isFormValid: function() {
                return _.keys(this.fields).every(key => this.fields[key].valid);
            },
            hasErrors: function() {
                return _.keys(this.fields).some(key => !this.fields[key].valid && this.fields[key].touched);
            },
        },
        watch: {
            isFormValid: function () {
                if (this.skipInitialUpdate) {
                    this.addressChanged();
                }
            },
        },
        created: function () {
            if (!_.isEmpty( this.defaultValues )) {
                this.address = this.defaultValues;
            }

            EventBus.$on('checkout-page-submit-clicked', () => {
                this.validate();
                this.addressChanged();
            });
        },
        mounted: function() {
            if (this.skipInitialUpdate) {
                this.addressChanged();
            }
            this.refresh += 1;

            if (this.formName === 'billing') {
              const customerTypes = document.querySelectorAll('[name="checkout-customer"]');
              [...customerTypes].forEach(input => {
                input.addEventListener('change', () => {
                  for (const [key, field] of Object.entries(this.formFields)) {
                    if (key === 'billing_company_id' || key === 'billing_company') {
                      const isNewCorporate = document.querySelector('.checkout__header__field :checked').value.endsWith('-company');
                      if (isNewCorporate) {
                        field.required = true;
                      } else {
                        field.required = false;
                      }
                      this.getValidations(field, key);
                    }
                  }
                  this.refresh += 1;
                });
              });
            }
        },
        methods: {
            getIndex(index) {
                return index.replace(this.formName + "_", '');
            },
            fieldChanged() {
                this.addressChanged();
            },
            fieldFocusOut(key) {
                this.$validator.validate(key);
            },
            addressChanged() {
                let response = {};
                response['address'] = _.clone(this.address);
                response['errors'] = this.errors;
                response['valid'] = this.isFormValid;

                if (this.formName === 'shipping' && !this.useShippingAddress) {
                    response['valid'] = true; //Errors in shipping address, won't matter if shipping address is not in use.
                }

                response.addressType = this.formName;
                response.useForShipping = this.formName === 'shipping' && this.useShippingAddress;

                if (this.formName === 'shipping') {
                    this.$store.commit('setShippingAddress', response);
                } else if(this.formName === 'billing') {
                    this.$store.commit('setBillingAddress', response);
                }

                this.$emit('change', response);
            },
            getValidations(field, key) {
                let validations = [];

                if (field.required) {
                    validations.push('required');
                }
                if (field.type === 'email') {
                    validations.push('email');
                } else if (key === 'billing_company_id') {
                    validations.push('businessid');
                } else if (key === 'billing_phone') {
                    validations.push('phone-fi');
                }

                return validations.join('|');
            },
            setAddress(address) {
                this.address = _.clone(address);
            },
            validate() {
                return this.$validator.validate();
            },
        },
    }
</script>
